// @ts-nocheck
import React, { useState } from 'react';
import type { AdminPage } from '../../types/admin';

interface AdminSidebarProps {
  activePage: AdminPage | 'media' | 'analytics' | 'branding';
  onNavigate: (page: AdminPage | 'media' | 'analytics' | 'branding') => void;
  isOpen: boolean;
  onToggle: () => void;
}

const primaryMenu: Array<{ id: AdminPage | 'media' | 'analytics'; label: string; icon: string }> = [
  { id: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
  { id: 'users', label: 'Users', icon: 'people' },
  { id: 'topics', label: 'Law Topics', icon: 'topic' },
  { id: 'subscriptions', label: 'Subscriptions', icon: 'subscriptions' },
  { id: 'notifications', label: 'Notifications', icon: 'notifications' },
  { id: 'emailTemplates', label: 'Email Templates', icon: 'mail_merge' },
  { id: 'contacts', label: 'Contacts', icon: 'contact_mail' },
  { id: 'news', label: 'News & Updates', icon: 'article' },
  { id: 'legalradar', label: 'Legal Radar', icon: 'balance' },
];

const secondaryMenu: Array<{ id: AdminPage | 'media' | 'analytics' | 'branding'; label: string; icon: string }> = [
  { id: 'media', label: 'Media Library', icon: 'folder' },
  { id: 'analytics', label: 'Analytics', icon: 'analytics' },
  { id: 'branding', label: 'Branding', icon: 'palette' },
  { id: 'settings', label: 'Settings', icon: 'settings' },
];

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ 
  activePage, 
  onNavigate, 
  isOpen, 
  onToggle 
}) => {
  const [showAll, setShowAll] = useState(false);

  const handleNavigate = (page: AdminPage | 'media' | 'analytics' | 'branding') => {
    onNavigate(page);
    if (window.innerWidth < 1024) {
      onToggle();
    }
  };

  const renderMenuButton = (item: { id: AdminPage | 'media' | 'analytics' | 'branding'; label: string; icon: string }) => (
    <li key={item.id}>
      <button
        onClick={() => handleNavigate(item.id)}
        className={`
          w-full flex items-center gap-3 px-4 py-2 sm:py-2.5 lg:py-2 rounded-lg
          transition-colors duration-200 min-h-[40px] touch-manipulation
          ${
            activePage === item.id
              ? 'bg-luna-secondary/20 text-luna-secondary dark:text-luna-secondary'
              : 'text-luna-text-light/70 dark:text-luna-text-dark/70 hover:bg-luna-secondary/10 hover:text-luna-text-light dark:hover:text-luna-text-dark active:bg-luna-secondary/20'
          }
        `}
      >
        <span className="material-symbols-outlined text-xl">{item.icon}</span>
        <span className="font-medium truncate">{item.label}</span>
      </button>
    </li>
  );

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        id="admin-sidebar"
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-white dark:bg-luna-primary border-r border-luna-secondary/30
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
        `}
        role="navigation"
        aria-label="Admin panel navigation"
      >
        {/* Header */}
        <div className="p-4 border-b border-luna-secondary/30 flex items-center justify-between">
          <h2 className="text-xl font-black text-luna-text-light dark:text-luna-text-dark">
            Admin Panel
          </h2>
          <button
            onClick={onToggle}
            className="lg:hidden p-2 rounded-lg hover:bg-luna-secondary/10 text-luna-text-light dark:text-luna-text-dark"
            aria-label="Close sidebar"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4" aria-label="Admin pages">
          <ul className="space-y-1">
            {primaryMenu.map(renderMenuButton)}

            <li className="mt-6">
              <button
                onClick={() => setShowAll((prev) => !prev)}
                className="w-full flex items-center justify-between px-4 py-2 rounded-lg border border-luna-secondary/30 text-sm font-semibold text-luna-text-light dark:text-luna-text-dark"
                aria-expanded={showAll}
              >
                <span className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-base">more_horiz</span>
                  More tools
                </span>
                <span className="material-symbols-outlined text-sm">
                  {showAll ? 'expand_less' : 'expand_more'}
                </span>
              </button>
            </li>

            {showAll &&
              secondaryMenu.map(renderMenuButton)}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-luna-secondary/30 space-y-2">
          <div className="text-xs text-luna-text-light/50 dark:text-luna-text-dark/50">
            LRDEnE Admin v2.0
          </div>
          <div className="text-xs text-luna-text-light/40 dark:text-luna-text-dark/40">
            <div className="mb-1 font-medium">Shortcuts:</div>
            <div className="space-y-0.5">
              <div>⌘/Ctrl + 1-9: Navigate</div>
              <div>⌘/Ctrl + M: Toggle menu</div>
              <div>Esc: Close sidebar</div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};
